import { foldCostPeriods, pickT } from "@repo/shared";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUserLanguage } from "@/lib/i18n/context";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { CostSnapshot } from "@/lib/types/flow";
import { getRelativeTime } from "@/lib/utils";
import { T } from "./cost-table-widget.i18n";

type CostTableWidgetProps = {
  snapshot: CostSnapshot;
  language?: string;
  /** QP-208 — render the optional "After N years" holding-period column. */
  showHoldingPeriod?: boolean;
  /** QP-208 — holding-period length in years (default 10, capped at series length). */
  holdingPeriodYears?: number;
};

/**
 * Format a number as currency
 */
type FormatNumberFn = (
  value: number,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
) => string;

function formatCurrency(amount: number, currency: string, fmt: FormatNumberFn): string {
  return `${fmt(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

export function CostTableWidget({
  snapshot,
  language,
  showHoldingPeriod = false,
  holdingPeriodYears = 10,
}: CostTableWidgetProps) {
  const { userLanguage } = useUserLanguage();
  const { formatNumber } = useRegionalFormat();
  const lang = language || userLanguage;
  const t = pickT(T, lang);

  const firstYear = snapshot.firstYearCost;

  if (!firstYear) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">{t.noCostDataAvailable}</p>
      </Card>
    );
  }

  // Falls back to a single-column table when the snapshot predates QP-208
  // (rolling entries without per-period fields make foldCostPeriods return null).
  const holdingPeriod = showHoldingPeriod
    ? foldCostPeriods(snapshot.yearlyCostsRolling, holdingPeriodYears)
    : null;

  const rows: { label: string; firstYear: string; holdingPeriod?: string }[] = [
    {
      label: t.deposits,
      firstYear: formatCurrency(firstYear.depositAmount, snapshot.currency, formatNumber),
      holdingPeriod: holdingPeriod
        ? formatCurrency(holdingPeriod.depositAmount, snapshot.currency, formatNumber)
        : undefined,
    },
    {
      label: t.expectedValueBeforeCost,
      firstYear: formatCurrency(firstYear.accountValueExCost, snapshot.currency, formatNumber),
      holdingPeriod: holdingPeriod
        ? formatCurrency(holdingPeriod.accountValueExCost, snapshot.currency, formatNumber)
        : undefined,
    },
    {
      label: t.expectedValueAfterCost,
      firstYear: formatCurrency(firstYear.accountValue, snapshot.currency, formatNumber),
      holdingPeriod: holdingPeriod
        ? formatCurrency(holdingPeriod.accountValue, snapshot.currency, formatNumber)
        : undefined,
    },
    {
      label: t.reductionInReturn,
      firstYear: `${firstYear.costEffectPct} %`,
      holdingPeriod: holdingPeriod ? `${holdingPeriod.costEffectPct} %` : undefined,
    },
    {
      label: firstYear.expectedReturnPct
        ? `${t.expectedReturn} (${firstYear.expectedReturnPct}%)`
        : t.expectedReturn,
      firstYear: formatCurrency(firstYear.returnAmount, snapshot.currency, formatNumber),
      holdingPeriod: holdingPeriod
        ? formatCurrency(holdingPeriod.returnAmount, snapshot.currency, formatNumber)
        : undefined,
    },
    {
      label: t.expectedReturnAfterCost,
      firstYear: `${firstYear.returnAmountPct} %`,
      holdingPeriod: holdingPeriod ? `${holdingPeriod.returnAmountPct} %` : undefined,
    },
  ];

  return (
    <div className="w-full space-y-4">
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.description}</TableHead>
            <TableHead className="text-right">{t.afterOneYear}</TableHead>
            {holdingPeriod && (
              <TableHead className="text-right">{t.afterNYears(holdingPeriod.years)}</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell className="font-medium">{row.label}</TableCell>
              <TableCell className="text-right">{row.firstYear}</TableCell>
              {holdingPeriod && <TableCell className="text-right">{row.holdingPeriod}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {snapshot.timestamp && (
        <div className="text-xs text-muted-foreground">
          {t.calculated} {getRelativeTime(snapshot.timestamp, lang)}
        </div>
      )}
    </div>
  );
}
